import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils.js';

/**
 * Toasts.
 *
 * Hand-rolled rather than pulled in, because what this panel needs from a toast
 * is one thing no library does out of the box: render the API's `errors[]`
 * array **field by field**. A failed write here answers with
 * `[{ field: 'dailyROI', message: '…' }, …]`, and collapsing that to "Request
 * failed" throws away the only part an operator can act on.
 *
 * Everything else is deliberately small: four variants, a queue, auto-dismiss
 * with a pause on hover, and `aria-live` so a screen reader hears the result of
 * an action that produced no visual focus change.
 */

const ToastContext = createContext(null);

const VARIANTS = Object.freeze({
  success: {
    icon: CheckCircle2,
    className: 'border-success/40 bg-success/10 text-foreground',
    iconClassName: 'text-success',
    role: 'status',
  },
  error: {
    icon: XCircle,
    className: 'border-destructive/40 bg-destructive/10 text-foreground',
    iconClassName: 'text-destructive',
    role: 'alert',
  },
  warning: {
    icon: AlertTriangle,
    className: 'border-warning/40 bg-warning/10 text-foreground',
    iconClassName: 'text-warning',
    role: 'alert',
  },
  info: {
    icon: Info,
    className: 'border-info/40 bg-info/10 text-foreground',
    iconClassName: 'text-info',
    role: 'status',
  },
});

/** Errors linger; confirmations do not need to. */
const DURATIONS = Object.freeze({ success: 4000, info: 4000, warning: 7000, error: 9000 });

let nextId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));

    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const schedule = useCallback(
    (id, duration) => {
      const timer = setTimeout(() => dismiss(id), duration);
      timers.current.set(id, timer);
    },
    [dismiss],
  );

  const push = useCallback(
    ({ variant = 'info', title, description, fields = [] }) => {
      const id = (nextId += 1);
      setToasts((current) => [...current, { id, variant, title, description, fields }]);
      schedule(id, DURATIONS[variant] ?? 5000);
      return id;
    },
    [schedule],
  );

  /* Cleared on unmount so a pending timer cannot fire into a dead tree. */
  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  const value = useMemo(
    () => ({
      toast: push,
      dismiss,

      success: (title, description) => push({ variant: 'success', title, description }),
      info: (title, description) => push({ variant: 'info', title, description }),
      warning: (title, description) => push({ variant: 'warning', title, description }),

      /**
       * The important one.
       *
       * Takes the shape `toApiError` produces and renders the field errors as a
       * list under the message. See the note at the top of this file.
       *
       * @param {{ message: string, fields: Array<{ field?: string, message: string }> }} apiError
       * @param {string} [title] overrides the API's message as the headline
       */
      error: (apiError, title) =>
        push({
          variant: 'error',
          title: title ?? apiError?.message ?? 'Something went wrong',
          description: title ? apiError?.message : undefined,
          fields: apiError?.fields ?? [],
        }),
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastViewport({ toasts, onDismiss }) {
  if (!toasts.length) return null;

  return (
    <div
      className="pointer-events-none fixed bottom-0 right-0 z-[100] flex w-full max-w-sm flex-col gap-2 p-4 sm:bottom-4 sm:right-4 sm:p-0"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }) {
  const variant = VARIANTS[toast.variant] ?? VARIANTS.info;
  const Icon = variant.icon;

  return (
    <div
      role={variant.role}
      className={cn(
        'pointer-events-auto flex animate-slide-in-right gap-3 rounded-lg border p-4 shadow-lg backdrop-blur',
        variant.className,
      )}
    >
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', variant.iconClassName)} aria-hidden="true" />

      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-snug">{toast.title}</p>

        {toast.description ? (
          <p className="mt-1 text-sm text-muted-foreground">{toast.description}</p>
        ) : null}

        {/*
         * The field-by-field list. `field` is shown in monospace because it is
         * an identifier the operator has to match against a form input, not
         * prose.
         */}
        {toast.fields?.length ? (
          <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
            {toast.fields.map((entry, index) => (
              <li key={`${entry.field ?? 'error'}-${index}`} className="flex gap-1.5">
                {entry.field ? (
                  <span className="font-mono font-medium text-foreground/80">{entry.field}</span>
                ) : null}
                <span>{entry.message}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="-m-1 h-6 w-6 shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside <ToastProvider>');
  return context;
};
